import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import useSocietyProviderService from '../../../app/services/hooks/useSocietyProviderService';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../../components/default/DefaultCard';
import LazyLoading from '../../../components/LazyLoading';
import { AppContext } from '../../../contexts/AppContext';
import SocietyContactContainer from '../../Societies/components/SocietyContactContainer';

export interface BusinessNameDetail {
    id: number;
    name: string | null;
    rut: string | null;
    email: string | null;
    phone_code: string | null;
    phone: string | null;
    address: string | null;
    country: string | null;
    fantasy_name: string | null;
    website: string | null;
}

export interface ProviderDetailForEdit {
    id: number;
    business_name_id: number | null;
    business_name: string;
    business_name_detail: BusinessNameDetail | null;
}

type RouterParams = {
    id: string;
};

const ProviderEditPage = () => {
    const { id } = useParams<RouterParams>();
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { editSocietyProvider, fetchingEditSocietyProvider } = useSocietyProviderService();

    const [providerDetail, setProviderDetail] = useState<ProviderDetailForEdit | null>(null);

    useEffect(() => {
        loadData(parseInt(id));
    }, [id]);

    const loadData = (societyProviderIdParam: number) => {
        if (showLoading) showLoading('loading', 'Cargando proveedor...');
        editSocietyProvider(societyProviderIdParam, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                const detail = response.data.provider_detail;
                if (detail) {
                    setProviderDetail({
                        id: detail.id,
                        business_name_id: detail.business_name_id ?? null,
                        business_name: detail.business_name || '',
                        business_name_detail: detail.business_name_detail ?? null
                    });
                } else {
                    setProviderDetail(null);
                }
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (response.data?.to_dashboard) {
                    history.push('/');
                } else {
                    history.push('/providers');
                }
            }
        });
    };

    const breadcrumbs: BreadcrumbsItem[] = [
        {
            name: 'Proveedores',
            url: '/providers',
            isActive: false
        },
        {
            name: 'Editar #' + id,
            url: '/providers/' + id + '/edit',
            isActive: true
        }
    ];

    const displayName = providerDetail?.business_name || 'Proveedor';

    if (fetchingEditSocietyProvider) {
        return (
            <>
                <Breadcrumbs pageSection="Editar proveedor" breadcrumbs={breadcrumbs} />
                <DefaultCard>
                    <LazyLoading height={300} />
                </DefaultCard>
            </>
        );
    }

    return (
        <>
            <Breadcrumbs pageSection={`Editar - ${displayName}`} breadcrumbs={breadcrumbs} />

            <div className="row">
                <div className="col-md-8">
                    <DefaultCard>
                        <div className="h3 mt-0 mb-2 card-title">Datos Generales</div>
                        <p className="text-muted small mb-4">
                            Información de la razón social asociada al proveedor.
                        </p>
                        {providerDetail?.business_name_detail ? (
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">Razón Social</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={providerDetail.business_name_detail.name || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">RUT</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={providerDetail.business_name_detail.rut || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">Nombre de fantasía</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={providerDetail.business_name_detail.fantasy_name || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">País</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={providerDetail.business_name_detail.country || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">Dirección</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={providerDetail.business_name_detail.address || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">Teléfono</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={[
                                            providerDetail.business_name_detail.phone_code,
                                            providerDetail.business_name_detail.phone
                                        ].filter(Boolean).join(' ') || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">Sitio web</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={
                                            providerDetail.business_name_detail.website &&
                                            providerDetail.business_name_detail.website !== 'null'
                                                ? providerDetail.business_name_detail.website
                                                : ''
                                        }
                                        readOnly
                                        placeholder="—"
                                    />
                                    {providerDetail.business_name_detail.website &&
                                    providerDetail.business_name_detail.website !== 'null' ? (
                                        <a
                                            href={providerDetail.business_name_detail.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="small mt-1 d-inline-block"
                                        >
                                            Abrir enlace
                                        </a>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label fw-semibold text-secondary small text-uppercase">Razón Social</label>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm bg-light"
                                        value={providerDetail?.business_name || ''}
                                        readOnly
                                        placeholder="—"
                                    />
                                </div>
                            </div>
                        )}
                    </DefaultCard>
                </div>
            </div>

            {providerDetail?.business_name_id ? (
                <SocietyContactContainer
                    societyId={0}
                    businessNameId={providerDetail.business_name_id}
                />
            ) : (
                <DefaultCard>
                    <div className="h3 mt-0 mb-2 card-title">Contactos</div>
                    <p className="text-muted mb-0">
                        El proveedor no tiene una razón social asociada. Asocie una razón social al proveedor para gestionar contactos.
                    </p>
                </DefaultCard>
            )}
        </>
    );
};

export default ProviderEditPage;
